import React, { useEffect, useRef, useState } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { EllipsisVerticalIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import ChatAvatar from '../chat/ChatAvatar';
import RolePill from './RolePill';
import ReactionRow from './ReactionRow';
import type { Comment, ReactionKind } from '../../types/comments';

export interface CommentItemProps {
  comment: Comment;
  /** Current viewer id — drives "You" badge and edit/delete affordances. */
  viewerId: string | null;
  /** Top-level vs reply — only top-level rows show the Reply button. */
  isTopLevel: boolean;
  onReply?: () => void;
  onEdit: (next: string) => void;
  onDelete: () => void;
  onToggleReaction: (kind: ReactionKind, emoji?: string) => void;
}

const MAX_BODY = 10000;

/**
 * Visual row for a single comment. Handles in-place edit (textarea swap),
 * tombstone rendering for soft-deleted comments, and the kebab menu for
 * the author. Reactions and Reply are delegated to `ReactionRow`.
 */
export default function CommentItem({
  comment,
  viewerId,
  isTopLevel,
  onReply,
  onEdit,
  onDelete,
  onToggleReaction,
}: CommentItemProps) {
  const isAuthor = !!viewerId && comment.author.id === viewerId;
  const isDeleted = comment.deleted_at != null;
  const isPending = comment.pending === true;

  const [isEditing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);

  useEffect(() => {
    if (!isEditing) setDraft(comment.body);
  }, [comment.body, isEditing]);

  const trimmedDraft = draft.trim();
  const canSubmit =
    trimmedDraft.length > 0 &&
    trimmedDraft.length <= MAX_BODY &&
    trimmedDraft !== comment.body;

  const handleSave = () => {
    if (!canSubmit) return;
    onEdit(trimmedDraft);
    setEditing(false);
  };

  return (
    <div className="flex gap-3 items-start" data-pending={isPending || undefined}>
      <ChatAvatar
        src={comment.author.avatar_url}
        name={comment.author.name}
        size={32}
      />
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold text-text-primary truncate max-w-[12rem]">
            {comment.author.name}
          </span>
          <RolePill role={comment.author.role} isYou={isAuthor} />
          {comment.created_at && (
            <span className="text-xs text-text-tertiary">
              {formatRelative(comment.created_at)}
            </span>
          )}
          {comment.edited_at && !isDeleted && (
            <span className="text-xs text-text-tertiary italic">(edited)</span>
          )}
          {isAuthor && !isDeleted && !isEditing && (
            <div className="ml-auto">
              <CommentMenu
                onEdit={() => setEditing(true)}
                onDelete={onDelete}
              />
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-2">
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={MAX_BODY}
              rows={3}
              className="block w-full rounded-lg border border-border-primary bg-surface-primary px-3 py-2 text-sm text-text-primary placeholder-text-tertiary resize-y min-h-[64px] focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSubmit}
                className="inline-flex items-center justify-center h-8 px-3 rounded-full bg-primary-500 text-xs font-bold disabled:opacity-50 hover:bg-primary-600 dark:hover:bg-primary-400 active:scale-[0.97] transition-[background-color,transform] duration-fast ease-out-strong"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setDraft(comment.body);
                }}
                className="inline-flex items-center justify-center h-8 px-3 rounded-full text-xs font-medium text-text-secondary hover:bg-[rgba(3,2,19,0.06)] dark:hover:bg-white/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-fast ease-out-strong"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p
            className={`text-sm leading-[1.625] whitespace-pre-wrap break-words ${
              isDeleted ? 'italic text-text-tertiary' : 'text-text-primary/90'
            }`}
          >
            {comment.body}
          </p>
        )}

        {!isDeleted && !isEditing && (
          <ReactionRow
            comment={comment}
            canReact={!isPending && !!viewerId}
            canReply={isTopLevel && !isPending}
            onToggleLike={() => onToggleReaction('like')}
            onToggleDislike={() => onToggleReaction('dislike')}
            onReply={onReply}
          />
        )}
      </div>
    </div>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return formatDistanceToNowStrict(d, { addSuffix: true });
}

interface CommentMenuProps {
  onEdit: () => void;
  onDelete: () => void;
}

function CommentMenu({ onEdit, onDelete }: CommentMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Comment actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-text-tertiary hover:bg-[rgba(3,2,19,0.06)] dark:hover:bg-white/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-fast ease-out-strong"
      >
        <EllipsisVerticalIcon className="w-4 h-4" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-10 min-w-[140px] rounded-lg border border-border-primary bg-surface-elevated shadow-md py-1 origin-top-right"
          style={{
            transitionTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)',
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              onEdit();
              setOpen(false);
            }}
            className="flex items-center gap-2 w-full px-3 py-2 text-xs text-text-primary hover:bg-[rgba(3,2,19,0.06)] dark:hover:bg-white/[0.06] text-left"
          >
            <PencilSquareIcon className="w-4 h-4" />
            Edit
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              onDelete();
              setOpen(false);
            }}
            className="flex items-center gap-2 w-full px-3 py-2 text-xs text-error-600 hover:bg-error-500/10 text-left"
          >
            <TrashIcon className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
